import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import { PayeeEngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { Nullable } from "primereact/ts-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  queriesPayeeEngagements,
  queriesPayeeRows,
  queriesPayees
} from "@query";
import { selectorPayeeDisplayName } from "../../../../shared/selectors/selectorPayeeDisplayName";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { useXWingspanPayerId } from "../../routes/RoutePayeeDetails/useXWingspanPayerId";
import { useCreateContractorModal } from "../CreateContractorModal/useCreateContractorModal";
import { useAccountAccess } from "../../../../v3/hooks/useAccountAccess";

type Value = {
  payeeId: string;
  payerPayeeEngagementId: string;
};
type Props = {
  value: Nullable<Value>;
  onChange: (value: Nullable<Value>) => void;
  defaultSearchText?: string;
} & Omit<WSSelectSearchProps, "options" | "value" | "onChange">;

export const SelectPayeeEngagement: React.FC<Props> = ({
  value,
  onChange,
  defaultSearchText,
  ...otherProps
}) => {
  const modalCreateContractor = useCreateContractorModal();
  const [searchText, setSearchText] = useState(defaultSearchText);
  const [selectValue, setSelectValue] =
    useState<Nullable<WSSelectOption>>(null);

  const queryPayeeRows = queriesPayeeRows.searchPayeeRows.usePaginatedQuery({
    filter: {
      searchString: searchText ? { eq: searchText } : undefined
    }
  });

  const { hasScope } = useAccountAccess();

  const xWingspanAccount = useXWingspanPayerId();
  const queryPayee = queriesPayees.getPayee.useQuery(
    { payeeId: value?.payeeId as string, xWingspanAccount },
    { enabled: !!value?.payeeId }
  );
  const queryPayeeEngagement =
    queriesPayeeEngagements.getPayeeEngagement.useQuery(
      {
        payeeId: value?.payeeId as string,
        payeeEngagementId: value?.payerPayeeEngagementId as string,
        xWingspanAccount
      },
      {
        enabled: !!value?.payeeId && !!value?.payerPayeeEngagementId
      }
    );

  const payee = queryPayee.data;
  const payeeEngagement = queryPayeeEngagement.data;

  useEffect(() => {
    if (!payee || !payeeEngagement) {
      if (value) {
        return;
      }

      setSelectValue(null);
      return;
    }

    const payeeName = selectorPayeeDisplayName(payee);

    setSelectValue({
      value: payee.id! + "/" + payeeEngagement.id,
      label: `${payeeName} • ${payeeEngagement.engagementName}`,
      labels: payee.email ? [payee.email] : [],
      avatar: {
        type: "text",
        text: payeeName
      }
    });
  }, [payee, payeeEngagement, value]);

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    (queryPayeeRows.flatData ?? []).forEach(payee => {
      const name = selectorSearchRowName(payee);

      // Active, non-duplicate engagements are selectable. The v1 `labels.duplicate`
      // flag is carried on the v3 row engagement's `metadata` map (v1 `labels`
      // → v3 `metadata`); metadata values are strings, so the boolean `true`
      // becomes the string `"true"`.
      const filteredEngagements = (payee.engagements ?? []).filter(
        e =>
          e.status === PayeeEngagementStatus.ACTIVATED &&
          e.metadata?.duplicate !== "true"
      );

      filteredEngagements.forEach(e => {
        options.push({
          value: payee.id + "/" + e.payeeEngagementId,
          // Default engagements have no distinct name to show alongside the payee.
          label: [name, e.isDefault ? undefined : e.engagementName]
            .filter(Boolean)
            .join(" • "),
          labels: payee.email ? [payee.email] : [],
          avatar: {
            type: "text",
            text: name
          }
        });
      });
    });

    return options;
  }, [queryPayeeRows.flatData]);

  const onReachMenuEnd = useCallback(() => {
    if (queryPayeeRows.hasNextPage) {
      queryPayeeRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayeeRows.hasNextPage]);

  const handleChange = useCallback(
    newSelectValue => {
      if (newSelectValue === null) {
        onChange(null);
        setSelectValue(null);
        return;
      }

      const [payeeId, payerPayeeEngagementId] = newSelectValue.value.split("/");
      onChange({
        payeeId,
        payerPayeeEngagementId
      });
      setSelectValue(newSelectValue);
    },
    [onChange]
  );

  return (
    <WSSelectSearch
      {...otherProps}
      value={selectValue}
      onChange={handleChange}
      // label="Payee"
      placeholder="Select an option"
      options={options}
      status={queryPayeeRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        queryPayeeRows.isFetching && !queryPayeeRows.isFetchingNextPage
      }
      onReachMenuEnd={onReachMenuEnd}
      menuFooterText={
        hasScope("payments.payee")
          ? "Don't see a Contractor name? Add a new contractor to your contractor list."
          : undefined
      }
      menuFooterAction={
        hasScope("payments.payee")
          ? {
              label: "Add contractor",
              onClick: () => {
                modalCreateContractor.open({
                  async onSuccess(payee) {
                    if (payee) {
                      const engagementList =
                        await queriesPayeeEngagements.listPayeeEngagements.fetchQueryData(
                          { payeeId: payee.id! }
                        );
                      const engagements = engagementList.data ?? [];

                      if (engagements.length) {
                        const payeeEngagement = engagements[0];

                        const payeeName =
                          payee.profile?.displayName || payee.email || "";

                        handleChange({
                          value: payee.id + "/" + payeeEngagement.id,
                          label: `${payeeName} • ${payeeEngagement.engagementName}`,
                          labels: payee.email ? [payee.email] : [],
                          avatar: {
                            type: "text",
                            text: payeeName
                          }
                        });
                      }
                    }
                  }
                });
              }
            }
          : undefined
      }
    />
  );
};
